import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import "@babylonjs/core/Particles/particleSystemComponent";
import { Constants } from "@babylonjs/core/Engines/constants";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ShipModelId } from "./shipModel";
import { onSparkFxChange, sparkFxFor, type SparkFx } from "./sparkFx";

/**
 * Engine flames, F-Zero GX style. At every nozzle: a white-hot glow, an inner
 * core and an outer blue plume that stretch with speed, flare and fatten when
 * boosting, and flicker; and sparks off the nozzles, shaped by the live
 * settings in sparkFx.ts, each design its own (tuned in the FX lab,
 * fxlab.html). The flame parts
 * are shared meshes drawn as instances (three draw calls for every racer's
 * flames together); each craft has one small particle system for its sparks.
 */

/** A nozzle in its model's glTF coordinates (nose +Z): centre and radius, and
 * for a slot rather than a round nozzle, its half-width and half-height. */
interface Nozzle {
  x: number;
  y: number;
  z: number;
  r: number;
  w?: number;
  h?: number;
}

/** Mirror a nozzle to both sides. */
const pair = (n: Nozzle): Nozzle[] => [n, { ...n, x: -n.x }];

/** Read off each design's rear view (models/<id>/<id>-pbr-rear.png) and the
 * rear-facing patches bake_pbr.py found (models/<id>/thrusters.json). */
const NOZZLES: Record<ShipModelId, Nozzle[]> = {
  needle: pair({ x: 0.84, y: 0.3, z: -2.25, r: 0.21 }),
  delta: [{ x: 0, y: 0.3, z: -2.3, r: 0.12, w: 0.32, h: 0.09 }],
  brute: pair({ x: 0.51, y: 0.37, z: -2.21, r: 0.21 }),
  striker: [{ x: 0, y: 0.4, z: -2.18, r: 0.18 }, ...pair({ x: 0.47, y: 0.36, z: -2.18, r: 0.1 })],
  fork: [...pair({ x: 0.23, y: 0.52, z: -2.29, r: 0.14 }), ...pair({ x: 0.23, y: 0.28, z: -2.13, r: 0.14 })],
  finback: [{ x: 0, y: 0.54, z: -2.29, r: 0.15 }, ...pair({ x: 0.24, y: 0.33, z: -2.29, r: 0.15 })],
  swift: pair({ x: 0.73, y: 0.35, z: -2.13, r: 0.12 }),
};

/** Plume length in nozzle radii: at rest, added at full speed, and the boost stretch. */
const LEN_IDLE = 2.2;
const LEN_SPEED = 5.5;
const BOOST_STRETCH = 1.9;
/** A spark's texture: across (its width) by along (its length). */
const SPARK_TEX_W = 64;
const SPARK_TEX_H = 128;
const DEG = Math.PI / 180;

interface Kit {
  plume: Mesh;
  core: Mesh;
  glow: Mesh;
}
const kits = new WeakMap<Scene, Kit>();

/** A gradient texture: stops of [position 0..1, css colour], down its height
 * or outward from its centre. */
function gradient(scene: Scene, name: string, stops: [number, string][], radial: boolean): DynamicTexture {
  const S = radial ? 128 : 256;
  const W = radial ? S : 8;
  const tex = new DynamicTexture(name, { width: W, height: S }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const g = radial ? ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2) : ctx.createLinearGradient(0, 0, 0, S);
  for (const [p, c] of stops) g.addColorStop(p, c);
  ctx.clearRect(0, 0, W, S);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, S);
  tex.hasAlpha = true;
  tex.update();
  return tex;
}

/** Paint a spark's texture from the settings. A streak: across its width a
 * hard-edged core with a glow either side, and along its length (head at the
 * top) a fade at each end and from head to tail. A dot: the same core and
 * glow, round (drawn squashed, since the canvas is twice as tall as wide). */
function drawSpark(tex: DynamicTexture, fx: SparkFx): void {
  const W = SPARK_TEX_W;
  const H = SPARK_TEX_H;
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const white = (a: number) => `rgba(255,255,255,${Math.min(1, Math.max(0, a))})`;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, W, H);
  if (fx.shape === "dot") {
    ctx.setTransform(1, 0, 0, H / W, 0, 0);
    const R = W / 2 - 1;
    const cr = Math.max(0.5, R * fx.core);
    const halo = ctx.createRadialGradient(W / 2, W / 2, cr, W / 2, W / 2, R);
    halo.addColorStop(0, white(fx.glow));
    halo.addColorStop(1, white(0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(W / 2, W / 2, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = white(1);
    ctx.beginPath();
    ctx.arc(W / 2, W / 2, cr, 0, Math.PI * 2);
    ctx.fill();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  } else {
    const cw = Math.max(2, Math.round(W * Math.min(1, Math.max(0, fx.core))));
    const x0 = (W - cw) / 2;
    if (fx.glow > 0 && cw < W) {
      const across = ctx.createLinearGradient(0, 0, W, 0);
      across.addColorStop(0, white(0));
      across.addColorStop(x0 / W, white(fx.glow));
      across.addColorStop((x0 + cw) / W, white(fx.glow));
      across.addColorStop(1, white(0));
      ctx.fillStyle = across;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.fillStyle = white(1);
    ctx.fillRect(x0, 0, cw, H);
    // the length: fades at the ends, and from head to tail
    const e = Math.min(0.49, Math.max(0, fx.endFade));
    const tail = 1 - Math.min(1, Math.max(0, fx.tailFade));
    const along = ctx.createLinearGradient(0, 0, 0, H);
    along.addColorStop(0, white(e > 0 ? 0 : 1));
    along.addColorStop(e, white(1));
    along.addColorStop(1 - e, white(tail));
    along.addColorStop(1, white(e > 0 ? 0 : tail));
    ctx.globalCompositeOperation = "destination-in";
    ctx.fillStyle = along;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
  }
  tex.update();
}

function color4(hex: string, a: number): Color4 {
  const c = Color3.FromHexString(hex);
  return new Color4(c.r, c.g, c.b, a);
}

/** Additive, unlit, seen from both sides. */
function flameMaterial(scene: Scene, name: string, tex: DynamicTexture): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.disableLighting = true;
  m.emissiveTexture = tex;
  m.opacityTexture = tex;
  m.emissiveColor = new Color3(1, 1, 1);
  m.diffuseColor = new Color3(0, 0, 0);
  m.specularColor = new Color3(0, 0, 0);
  m.alphaMode = Constants.ALPHA_ADD;
  m.backFaceCulling = false;
  m.disableDepthWrite = true;
  return m;
}

/** A cone with its base (radius 1) at z = 0 and its tip at z = -1, UV v = 0 at
 * the base and 1 at the tip. */
function cone(scene: Scene, name: string): Mesh {
  const m = MeshBuilder.CreateCylinder(name, { height: 1, diameterTop: 0, diameterBottom: 2, tessellation: 14, cap: Mesh.NO_CAP }, scene);
  m.rotation.x = -Math.PI / 2;
  m.position.z = -0.5;
  m.bakeCurrentTransformIntoVertices();
  const pos = m.getVerticesData(VertexBuffer.PositionKind)!;
  const uv: number[] = [];
  for (let i = 0; i < pos.length; i += 3) uv.push(0.5, Math.min(1, Math.max(0, -pos[i + 2])));
  m.setVerticesData(VertexBuffer.UVKind, uv);
  return m;
}

function kit(scene: Scene): Kit {
  let k = kits.get(scene);
  if (k) return k;
  const plume = cone(scene, "boostPlume");
  plume.material = flameMaterial(
    scene,
    "boostPlumeMat",
    gradient(scene, "boostPlumeTex", [[0, "rgba(170,235,255,0.95)"], [0.25, "rgba(70,170,255,0.6)"], [0.7, "rgba(40,90,255,0.18)"], [1, "rgba(30,60,255,0)"]], false)
  );
  const core = cone(scene, "boostCore");
  core.material = flameMaterial(
    scene,
    "boostCoreMat",
    gradient(scene, "boostCoreTex", [[0, "rgba(255,255,255,1)"], [0.35, "rgba(200,245,255,0.75)"], [1, "rgba(120,200,255,0)"]], false)
  );
  const glow = MeshBuilder.CreatePlane("boostGlow", { size: 1 }, scene);
  glow.material = flameMaterial(
    scene,
    "boostGlowMat",
    gradient(scene, "boostGlowTex", [[0, "rgba(255,255,255,1)"], [0.14, "rgba(200,245,255,0.85)"], [0.42, "rgba(70,170,255,0.3)"], [1, "rgba(40,120,255,0)"]], true)
  );
  for (const m of [plume, core, glow]) {
    m.isPickable = false;
    m.setEnabled(false); // sources only: the instances draw
  }
  k = { plume, core, glow };
  kits.set(scene, k);
  return k;
}

interface Part {
  n: Nozzle;
  anchor: TransformNode;
  plume: InstancedMesh;
  core: InstancedMesh;
  glow: InstancedMesh;
  /** The flame cone as last drawn: its base's half-width and half-height,
   * and its length. The sparks run along it. */
  rx: number;
  ry: number;
  len: number;
}

/** One craft's flames and sparks. */
export class Boosters {
  private readonly parts: Part[] = [];
  /** The model's normalising scale (glTF units to scene units). */
  private readonly scale: number;
  private readonly sparks: ParticleSystem;
  private readonly sparkTex: DynamicTexture;
  /** This design's live spark settings. */
  private readonly fx: SparkFx;
  /** The sparks' frame: the model's own. */
  private readonly emitter: Mesh;
  /** Where that frame was at the last update, and how it has moved since. */
  private readonly lastFrame = new Matrix();
  private readonly inv = new Matrix();
  private readonly moved = new Matrix();
  private placed = false;
  private t = Math.random() * 10;
  private boost = 0;

  /** `model` is what buildShipModel returned. Its one child is the glTF root,
   * which flips handedness with a negative scale; the flames hang off `model`
   * itself (a plain, positive-scale frame, nose +Z), so their billboards and
   * directions stay true however the craft turns. `maxSparks` caps how many
   * sparks can be alive at once. */
  constructor(scene: Scene, model: TransformNode, id: ShipModelId, maxSparks = 300) {
    const k = kit(scene);
    this.fx = sparkFxFor(id);
    const root = (model.getChildren()[0] as TransformNode | undefined) ?? model;
    // glTF coordinates → the model's own frame, once.
    const toModel = root.computeWorldMatrix(true).multiply(Matrix.Invert(model.computeWorldMatrix(true)));
    this.scale = Math.abs(root.scaling.x) || 1;
    for (const n of NOZZLES[id]) {
      const anchor = new TransformNode(`nozzle_${id}`, scene);
      anchor.parent = model;
      anchor.position = Vector3.TransformCoordinates(new Vector3(n.x, n.y, n.z - 0.02), toModel);
      const make = (src: Mesh, tag: string) => {
        const inst = src.createInstance(`${tag}_${id}`);
        inst.parent = anchor;
        inst.isPickable = false;
        return inst;
      };
      const glow = make(k.glow, "glow");
      glow.billboardMode = Mesh.BILLBOARDMODE_ALL;
      const rx = (n.w ?? n.r) * this.scale;
      const ry = (n.h ?? n.r) * this.scale;
      this.parts.push({ n, anchor, plume: make(k.plume, "plume"), core: make(k.core, "core"), glow, rx, ry, len: n.r * this.scale * LEN_IDLE });
    }

    // Sparks: each starts somewhere across a random nozzle, flies back and out
    // and dies, all as its design's settings say. They are world-space particles (a
    // stretched spark needs its direction in world space), emitted from an
    // empty mesh at the model's origin, and update() carries the live ones
    // rigidly along with the craft. Disposing the craft disposes that mesh,
    // and Babylon disposes an emitter's particle systems with it, texture
    // included: so each craft has its own texture.
    this.emitter = new Mesh(`sparkFrame_${id}`, scene);
    this.emitter.parent = model;
    this.emitter.isPickable = false;
    const ps = new ParticleSystem(`sparks_${id}`, maxSparks, scene);
    ps.emitter = this.emitter;
    this.sparkTex = new DynamicTexture(`boostSparkTex_${id}`, { width: SPARK_TEX_W, height: SPARK_TEX_H }, scene, true);
    this.sparkTex.hasAlpha = true;
    ps.particleTexture = this.sparkTex;
    ps.updateSpeed = 1 / 60; // with emit power 1: directions in units/s, lifetimes in seconds
    ps.minEmitPower = ps.maxEmitPower = 1;
    ps.minScaleX = ps.maxScaleX = 1;
    ps.gravity = new Vector3();
    ps.emitRate = 0;
    // Sparks come out of the engines: each leaves a random nozzle's mouth,
    // `start` of the way from its centre to its rim, and flies back along the
    // flame, `spread` off straight back (above 0 flaring out, so it leaves the
    // narrowing flame and runs alongside it), twisted up to `swirl` round the
    // axis. `reach` has it die by the flame's tip. A spark is placed and aimed
    // in the model's frame, then put into the world through the emitter's
    // matrix (the model's).
    const local = new Vector3();
    let born = this.parts[0];
    let ux = 1; // which way from the nozzle's centre it came out
    let uy = 0;
    ps.startPositionFunction = (m, pos) => {
      const fx = this.fx;
      born = this.parts[(Math.random() * this.parts.length) | 0];
      const a = Math.random() * Math.PI * 2;
      const ex = Math.cos(a) * (born.n.w ?? born.n.r);
      const ey = Math.sin(a) * (born.n.h ?? born.n.r);
      const er = Math.hypot(ex, ey) || 1e-6;
      ux = ex / er;
      uy = ey / er;
      const k = (fx.startMin + (fx.startMax - fx.startMin) * Math.random()) * this.scale;
      local.copyFrom(born.anchor.position);
      local.x += ex * k;
      local.y += ey * k;
      local.y += fx.offsetY * this.scale;
      local.z += fx.offsetZ * this.scale;
      Vector3.TransformCoordinatesToRef(local, m, pos);
    };
    ps.startDirectionFunction = (m, dir, particle) => {
      const fx = this.fx;
      const v = (fx.speedMin + (fx.speedMax - fx.speedMin) * Math.random()) * (1 + (fx.boostSpeed - 1) * this.boost) * this.scale;
      const phi = (fx.spreadMin + (fx.spreadMax - fx.spreadMin) * Math.random()) * DEG;
      const tw = (Math.random() * 2 - 1) * fx.swirl * DEG;
      const ox = ux * Math.cos(tw) - uy * Math.sin(tw);
      const oy = ux * Math.sin(tw) + uy * Math.cos(tw);
      const back = Math.cos(phi) * v;
      local.set(ox * Math.sin(phi) * v, oy * Math.sin(phi) * v, -back);
      Vector3.TransformNormalToRef(local, m, dir);
      if (fx.reach > 0 && back > 1e-6) particle.lifeTime = Math.min(particle.lifeTime, (fx.reach * born.len) / back);
    };
    ps.start();
    this.sparks = ps;
    this.applySparkLook();
    this.emitter.onDisposeObservable.add(onSparkFxChange((d) => d === id && this.applySparkLook()));
  }

  /** Match the sparks' texture, colours, shape and blending to the settings. */
  private applySparkLook(): void {
    const fx = this.fx;
    const ps = this.sparks;
    drawSpark(this.sparkTex, fx);
    ps.billboardMode = fx.shape === "dot" ? ParticleSystem.BILLBOARDMODE_ALL : ParticleSystem.BILLBOARDMODE_STRETCHED;
    // No pivot offset: for stretched particles Babylon adds translationPivot
    // twice, once scaled by the particle's size and once in world units, so a
    // pivot shifts every spark along its flight (-0.5 put them half a unit
    // toward the nose, over the hull in front of the engine). A spark sits
    // centred on its point; at a few pixels long that barely reaches back.
    ps.translationPivot = new Vector2(0, 0);
    ps.blendMode = fx.blend === "alpha" ? ParticleSystem.BLENDMODE_STANDARD : ParticleSystem.BLENDMODE_ADD;
    for (const g of [...(ps.getColorGradients() ?? [])]) ps.removeColorGradient(g.gradient);
    ps.addColorGradient(0, color4(fx.colorStart, fx.alphaStart));
    ps.addColorGradient(Math.min(0.99, Math.max(0.01, fx.midAt)), color4(fx.colorMid, fx.alphaMid));
    ps.addColorGradient(1, color4(fx.colorEnd, fx.alphaEnd));
    // where the jets sit on their nozzles
    for (const p of this.parts) {
      for (const m of [p.plume, p.core, p.glow]) m.position.set(0, fx.jetY * this.scale, fx.jetZ * this.scale);
    }
  }

  /** FX lab: run the sparks at this rate of time (1 normal, 0 frozen). */
  setTimeScale(k: number): void {
    this.sparks.updateSpeed = k / 60;
  }

  /** FX lab: show or hide the flames, to see the sparks on their own. */
  setFlamesVisible(on: boolean): void {
    for (const p of this.parts) {
      p.plume.isVisible = on;
      p.core.isVisible = on;
      p.glow.isVisible = on;
    }
  }

  /** How many sparks are alive. */
  get sparkCount(): number {
    return this.sparks.getActiveCount();
  }

  /** Call once the craft has moved this frame. `speed` is its speed over its
   * top speed; `boosting` while a boost fires. */
  update(dt: number, speed: number, boosting: boolean): void {
    this.t += dt;
    this.boost += ((boosting ? 1 : 0) - this.boost) * Math.min(1, dt * 8);
    const s = Math.min(1.2, Math.max(0, speed));
    for (let i = 0; i < this.parts.length; i++) {
      const part = this.parts[i];
      const { n, plume, core, glow } = part;
      // fast, uneven flicker, out of step between nozzles
      const f = 1 + 0.09 * Math.sin(this.t * 53 + i * 1.7) + 0.06 * Math.sin(this.t * 31.7 + i * 4.1);
      const len = n.r * this.scale * (LEN_IDLE + LEN_SPEED * s) * (1 + (BOOST_STRETCH - 1) * this.boost) * f;
      // Boosting fattens the flames too: seen from the chase camera they point
      // straight back at you, so it's the width and the glow that sell it.
      const fat = (1 + 0.35 * this.boost) * this.scale * 0.95;
      const rx = (n.w ?? n.r) * fat;
      const ry = (n.h ?? n.r) * fat;
      plume.scaling.set(rx, ry, len);
      part.rx = rx;
      part.ry = ry;
      part.len = len;
      core.scaling.set(rx * 0.55, ry * 0.55, len * 0.5);
      const g = Math.max(rx, ry) * (2.2 + 0.7 * s) * (1 + 0.35 * this.boost) * (0.85 + 0.15 * f);
      glow.scaling.set(g, g, g);
    }
    // The sparks' frame, current before the particles move this frame (forcing
    // it recomputes the craft's own transform above it too). The live sparks
    // move with it, as if fixed to the craft, so they stay behind its nozzles.
    const frame = this.emitter.computeWorldMatrix(true);
    if (this.placed) {
      this.lastFrame.invertToRef(this.inv);
      this.inv.multiplyToRef(frame, this.moved);
      for (const p of this.sparks.particles) {
        Vector3.TransformCoordinatesToRef(p.position, this.moved, p.position);
        Vector3.TransformNormalToRef(p.direction, this.moved, p.direction);
      }
    }
    this.lastFrame.copyFrom(frame);
    this.placed = true;
    // How many, how big and how long-lived, from the live settings.
    const fx = this.fx;
    const ps = this.sparks;
    const flow = 1 - fx.crackle + 2 * fx.crackle * Math.random();
    ps.emitRate = Math.max(0, (fx.rate * s + fx.boostRate * this.boost) * flow);
    const wide = this.scale * (1 + (fx.boostWidth - 1) * this.boost);
    ps.minSize = fx.widthMin * wide;
    ps.maxSize = fx.widthMax * wide;
    const long = fx.shape === "dot" ? 1 : 1 + (fx.boostLength - 1) * this.boost;
    ps.minScaleY = fx.shape === "dot" ? 1 : fx.lengthMin * long;
    ps.maxScaleY = fx.shape === "dot" ? 1 : fx.lengthMax * long;
    ps.minLifeTime = fx.lifeMin;
    ps.maxLifeTime = fx.lifeMax;
    ps.gravity.set(0, -fx.gravity * this.scale, 0);
  }
}
